// THE CLOSED VOCABULARIES a BYOK key is described by: which provider it reaches, what
// state it is in, whether it meters or is a flat subscription, and which tier band a model
// on it falls into.
// Split out of `../byok.ts`, which is now the barrel over this directory.

import { z } from 'zod';

export const BYOKProvider = {
  Anthropic: 'anthropic',
  OpenAI: 'openai',
  OpenRouter: 'openrouter',
  Gemini: 'gemini',
  Kimi: 'kimi',
  Zai: 'zai',
  Cerebras: 'cerebras',
  Minimax: 'minimax',
  // ── OpenAI-wire bearer third parties (arc 101 slice 03b) ──────────────────
  // Each speaks the OpenAI `/chat/completions` wire with a `Bearer` token, on a
  // fixed default host: no coding-plan alt-path, no always-on reasoning, so they
  // integrate exactly like `cerebras`/`minimax` (base URL swap). `BYOKKey.model`
  // carries the host's model id; the maintainer picks it (marketplace hosts —
  // `together`/`fireworks` — namespace models, so no cheap tier is hardcoded).
  Groq: 'groq',
  Xai: 'xai',
  Deepseek: 'deepseek',
  Together: 'together',
  Fireworks: 'fireworks',
  /**
   * Alibaba Cloud Model Studio (DashScope) on its OpenAI-compatible wire (arc 101
   * slice 02). NO default host: the endpoint is scoped to the customer's
   * workspace and region, so the key MUST carry `baseUrl`, and only a recognized
   * Model Studio endpoint is admitted (`vetProviderBaseUrl`,
   * `value-objects/llm-credential/wire.ts`).
   */
  Alibaba: 'alibaba',
  /** Anthropic-compatible router gateway; default base URL is handled in the wire policy. */
  Router: 'router',
  /** Any OpenAI-compatible endpoint; requires `baseUrl` on the key. */
  Custom: 'custom',
} as const;
export const BYOKProviderSchema = z.enum(BYOKProvider);
export type BYOKProvider = z.infer<typeof BYOKProviderSchema>;

export const BYOKStatus = {
  /** Captured but not yet checked against the provider (see #105). Never used by the agent loop. */
  Unverified: 'unverified',
  Valid: 'valid',
  Invalid: 'invalid',
  Revoked: 'revoked',
  // ── RECOVERABLE states (#2844) ───────────────────────────────────────────
  // The runtime has graded provider failures into this vocabulary since arc 101
  // slice 03 (`LlmKeyFailureReason`, `../value-objects/llm-credential`) and then
  // thrown the grade away, writing `invalid` — a TERMINAL state a human has to
  // undo — for a wallet that refills or a throttle that lifts. These two members
  // persist that grade instead: a key in either is OUT of the pool (every spend
  // read gates on `status = 'valid'`) but a verify can hand it straight back,
  // because both sit in the verify seam's source-state set.
  //
  // The VALUES are `LlmKeyFailureReason`'s, verbatim — one vocabulary, not a
  // second one coined beside it. They are spelled as literals rather than
  // imported because `value-objects/llm-credential` imports VALUES from this
  // module (`PROVIDER_WIRE` reads `BYOKProvider` at its own init), so a
  // value-level import back would be a TDZ cycle. The identity is PINNED by a
  // test (`byok.test.ts` § recoverable statuses): rename either side of the
  // pair and it fails, which is the same guarantee an import would give
  // without the cycle.
  /** No quota/credit left (402, or a 429 body naming a spent wallet). Refills; re-verify restores `valid`. */
  CreditsExhausted: 'credits_exhausted',
  /** Transient throttle (429 with no exhaustion marker). Lifts on its own; re-verify restores `valid`. */
  RateLimited: 'rate_limited',
} as const;
export const BYOKStatusSchema = z.enum(BYOKStatus);
export type BYOKStatus = z.infer<typeof BYOKStatusSchema>;

/**
 * The {@link BYOKStatus} members that are RECOVERABLE — a plain verify hands the
 * row back to `valid`, with no human action and no re-paste (#2844). Every other
 * member is terminal in some direction: `invalid` needs the cause fixed and a
 * deliberate re-verify, `revoked` is a human's decision with no in-platform path
 * back, and `unverified`/`valid` are not failures at all.
 *
 * Ordered so `REVOKED` reads as the deliberate tail: a revoked row is excluded
 * from EVERY recovery path on purpose (`ne(status, 'revoked')` at the rotate /
 * kind / verify seams), and this list is one of the places that exclusion is
 * visible from.
 */
export const RECOVERABLE_BYOK_STATUSES = [
  BYOKStatus.Unverified,
  BYOKStatus.Invalid,
  BYOKStatus.CreditsExhausted,
  BYOKStatus.RateLimited,
] as const satisfies readonly BYOKStatus[];

/**
 * Billing nature of a pool key (arc 101 slice 03). `metered` = pay-per-token;
 * `subscription` = a flat-rate coding-plan credential (Kimi Code / z.ai coding) —
 * quota-bounded, not USD-bounded, and carrying provider-ToS constraints on who may
 * run it. Derived at insert from `isFlatRateKey` (the `base_url` endpoint) and
 * surfaced (badge) in the dashboard; null on a legacy row never classified, where
 * the resolver falls back to the SAME URL inference so a null never mis-bills.
 */
export const BYOKKeyKind = {
  Metered: 'metered',
  Subscription: 'subscription',
} as const;
export const BYOKKeyKindSchema = z.enum(BYOKKeyKind);
export type BYOKKeyKind = z.infer<typeof BYOKKeyKindSchema>;

/**
 * Capability tier a pool key is tagged with (arc 101 slice 04, LLM pool; #510).
 * Routing works on tiers — not model names — so a task's tier need ("this is
 * coding → `heavy`") is portable across providers: the same envelope runs on
 * whatever tier-tagged key the account holds. The canonical ladder (#510),
 * cheapest → strongest, adopts the predecessor orchestrator's `job-class → tier`
 * table:
 *
 * - `fast`    — the cheap high-throughput model (CI triage, quick / mechanical work).
 * - `general` — the everyday working model (the default for a `direct` task).
 * - `smart`   — strong reasoning (debugging-QA, scout, hard review).
 * - `heavy`   — the strongest / long-context model for a BIG task (the
 *               bigger-task slot — new features, hard slices).
 *
 * A pool key still tags itself with one of these; a legacy `smart|general|fast`
 * tag keeps its meaning, and `heavy` is additive (an untagged key matches any
 * tier need). A tier never names a model: the concrete id is always one the
 * account named — the key's own model, or a `byok_key_models` row the account
 * tagged with this tier.
 */
export const BYOKTier = {
  Fast: 'fast',
  General: 'general',
  Smart: 'smart',
  Heavy: 'heavy',
} as const;
export const BYOKTierSchema = z.enum(BYOKTier);
export type BYOKTier = z.infer<typeof BYOKTierSchema>;
